/**
 * Undo/redo stacks with sealing and a capped depth.
 *
 * An entry holds one or more executed commands (several when it came from a
 * transaction). Only the *top* entry can absorb merges, and only while it is
 * unsealed. The bus seals it on undo, redo, transaction boundaries and
 * session end (review F-7 refinement).
 */
import type { ChangeScheduler } from "../core/ChangeScheduler";
import { VersionedStore } from "../core/store";
import type { Executed } from "./types";

export interface ExecutedCommand extends Executed {
  readonly type: string;
}

export interface HistoryEntry {
  readonly label: string;
  commands: ExecutedCommand[];
  sealed: boolean;
  /**
   * Came from a transaction. Transactions merge only with transactions and
   * lone commands only with lone commands, so a journal entry keeps its shape.
   */
  readonly grouped: boolean;
  /** The journal entry this history entry was recorded as, updated on merge. */
  readonly journalSeq: number | null;
}

/** What the toolbar and Edit menu read. */
export interface UndoState {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoLabel: string | null;
  readonly redoLabel: string | null;
  readonly undoDepth: number;
  readonly redoDepth: number;
}

export class UndoHistory {
  readonly state: VersionedStore<UndoState>;
  private readonly undoStack: HistoryEntry[] = [];
  private readonly redoStack: HistoryEntry[] = [];

  constructor(
    scheduler: ChangeScheduler,
    private readonly maxDepth: number,
  ) {
    this.state = new VersionedStore(scheduler, computeState([], []));
  }

  get top(): HistoryEntry | undefined {
    return this.undoStack.at(-1);
  }

  /** Pushes a new entry, sealing the previous top and invalidating redo. */
  push(entry: HistoryEntry): void {
    this.seal();
    this.undoStack.push(entry);
    if (this.undoStack.length > this.maxDepth) this.undoStack.shift();
    this.redoStack.length = 0;
    this.publish();
  }

  /** Called after the top entry's commands were merged in place. */
  touched(): void {
    this.publish();
  }

  seal(): void {
    const top = this.top;
    if (top) top.sealed = true;
  }

  popUndo(): HistoryEntry | undefined {
    this.seal();
    const entry = this.undoStack.pop();
    if (entry) {
      this.redoStack.push(entry);
      this.publish();
    }
    return entry;
  }

  popRedo(): HistoryEntry | undefined {
    this.seal();
    const entry = this.redoStack.pop();
    if (entry) {
      entry.sealed = true;
      this.undoStack.push(entry);
      this.publish();
    }
    return entry;
  }

  /** Puts an entry popped by `popUndo` back if undoing it failed. */
  restoreAfterFailedUndo(entry: HistoryEntry): void {
    this.redoStack.pop();
    this.undoStack.push(entry);
    this.publish();
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.publish();
  }

  private publish(): void {
    this.state.publish(computeState(this.undoStack, this.redoStack));
  }
}

function computeState(undo: readonly HistoryEntry[], redo: readonly HistoryEntry[]): UndoState {
  return {
    canUndo: undo.length > 0,
    canRedo: redo.length > 0,
    undoLabel: undo.at(-1)?.label ?? null,
    redoLabel: redo.at(-1)?.label ?? null,
    undoDepth: undo.length,
    redoDepth: redo.length,
  };
}
